// src/lib/UnifiedKVClient.ts
import { createClient } from "@vercel/kv";

import { SyncService } from "@/lib/storage/device-sync-service.ts";
import { VercelKVAdapter } from "@/lib/storage/remote-client-adaptor.ts";

import { KVOperations } from "./kvOperations.ts";
import { LocalDatabase, localDatabase } from "./local-database.ts";
import { createMockClient } from "./mock-vercel-client";
import {
  BaseRangeOptions,
  BatchOperation,
  exists,
  GetOptions,
  isValidZRangeByLex,
  isValidZRangeByRank,
  isValidZRangeByScore,
  KVClientStatus,
  RemoteClient,
  SyncMetadata,
  ZRangeOptions,
  ZSetResponse
} from "./types";
import { ZSetOperations } from "./zSetOperation.ts";

/**
 * UnifiedKVClient provides a unified interface for key-value operations,
 * handling both local and remote storage with offline support
 */

interface UnifiedKVClientOptions {
  syncInterval?: number;
}

/**
 * UnifiedKVClient provides a unified interface for key-value operations,
 * handling both local and remote storage with offline support
 */
export class UnifiedKVClient {
  private static instance: UnifiedKVClient | null = null;
  private static forceRefreshTimeout: number | null = null;
  private static isForceRefreshing = false;
  private initialized = false;
  private remoteClient: RemoteClient | null = null;
  private readonly db: LocalDatabase;
  private kvOps: KVOperations;
  private zsetOps: ZSetOperations;
  private syncInProgress = false;
  private readonly syncInterval: number;

  private constructor(options: UnifiedKVClientOptions = {}) {
    const { syncInterval = 5000 } = options;
    this.syncInterval = syncInterval;

    console.log("KV Client initialization starting...", {
      hasProcess: typeof window !== "undefined" && !!window.process,
      hasImportMeta: !!import.meta.env,
    });

    // Initialize process.env polyfill for Vite
    this.initializeProcessEnv();

    // Initialize components
    this.db = localDatabase;
    // this.remoteClient = this.initializeRemoteClient();
    //
    // // Initialize operations
    this.kvOps = new KVOperations(this.db, this.remoteClient);
    this.zsetOps = new ZSetOperations(this.db, this.remoteClient);
    //
    // // Set up synchronization
    //
    // this.setupNetworkListeners();
  }

  public static startForceRefresh(): Promise<void> {
    if (UnifiedKVClient.forceRefreshTimeout) {
      clearTimeout(UnifiedKVClient.forceRefreshTimeout);
    }
    console.log("Force refresh period started");
    UnifiedKVClient.isForceRefreshing = true;

    return new Promise((resolve) => {
      UnifiedKVClient.forceRefreshTimeout = window.setTimeout(() => {
        console.log("Force refresh period ended");
        UnifiedKVClient.isForceRefreshing = false;
        UnifiedKVClient.forceRefreshTimeout = null;
        resolve();
      }, 60000); // 1 minute timeout
    });
  }

  public static isRefreshing(): boolean {
    return UnifiedKVClient.isForceRefreshing;
  }

  public static getInstance(): UnifiedKVClient {
    if (!UnifiedKVClient.instance) {
      UnifiedKVClient.instance = new UnifiedKVClient();
    }
    return UnifiedKVClient.instance;
  }

  // Use `after` getting the instance
  public static async getInitializedInstance(): Promise<UnifiedKVClient> {
    const instance = UnifiedKVClient.getInstance();
    if (!instance.initialized) {
      await instance.init();
    }
    return instance;
  }

  /**
   * Initialization logic that needs to run asynchronously.
   */
  public async init(): Promise<void> {
    this.remoteClient = await this.initializeRemoteClient();

    // Initialize operations
    this.kvOps = new KVOperations(this.db, this.remoteClient);
    this.zsetOps = new ZSetOperations(this.db, this.remoteClient);

    this.setupNetworkListeners();
    this.startPeriodicSync();
  }

  public initialize(): void {
    if (this.initialized) return;
    console.log("Initializing KV client...");
    this.startPeriodicSync();
    this.initialized = true;
  }

  // KV Operations
  public async get<T>(
    key: string,
    options: GetOptions<T> = {},
  ): Promise<T | null> {
    if (UnifiedKVClient.isForceRefreshing) {
      options.forceRefresh = true;
    }

    const value = await this.kvOps.get<T>(key, options);

    if (!exists(value)) return null;

    // Custom validation if provided
    if (options.validate && !options.validate(value)) {
      throw new Error(`Invalid data format for key ${key}`);
    }

    // Custom transformation if provided
    if (options.transform) {
      return options.transform(value);
    }

    return value as T;
  }

  public async set<T>(key: string, value: T): Promise<void> {
    await this.kvOps.set(key, value);
  }

  public async delete(key: string): Promise<void> {
    await this.kvOps.delete(key);
  }

  public async scan(
    cursor = 0,
    options: { match?: string; count?: number } = {},
  ): Promise<[number, string[]]> {
    return this.kvOps.scan(cursor, options);
  }

  // ZSet Operations
  public async zadd<T>(key: string, score: number, member: T): Promise<void> {
    await this.zsetOps.zadd(key, score, member);
  }

  public async zrem<T>(key: string, member: T): Promise<void> {
    await this.zsetOps.zrem<T>(key, member);
  }

  public async zremrangebyscore(
    key: string,
    min: number,
    max: number,
  ): Promise<void> {
    await this.zsetOps.zremrangebyscore(key, min, max);
  }

  public async zrangeByRank<T>(
    key: string,
    start: number,
    end: number,
    options: BaseRangeOptions = {},
  ): Promise<ZSetResponse<T>[]> {
    if (UnifiedKVClient.isForceRefreshing) {
      options.forceRefresh = true;
    }
    return this.zsetOps.zrangeByRank<T>(key, start, end, options);
  }

  public async zrangeByScore<T>(
    key: string,
    min: number | "-inf" | "+inf",
    max: number | "-inf" | "+inf",
    options: BaseRangeOptions = {},
  ): Promise<ZSetResponse<T>[]> {
    if (UnifiedKVClient.isForceRefreshing) {
      options.forceRefresh = true;
    }
    return this.zsetOps.zrangeByScore<T>(key, min, max, options);
  }

  public async zrangeByLex<T>(
    key: string,
    min: `(${string}` | `[${string}` | "-" | "+",
    max: `(${string}` | `[${string}` | "-" | "+",
    options: BaseRangeOptions = {},
  ): Promise<ZSetResponse<T>[]> {
    if (UnifiedKVClient.isForceRefreshing) {
      options.forceRefresh = true;
    }
    return this.zsetOps.zrangeByLex<T>(key, min, max, options);
  }

  // Utility Operations
  public async getAllKeys(match?: string): Promise<string[]> {
    let cursor = 0;
    const allKeys: string[] = [];

    do {
      const [nextCursor, keys] = await this.scan(cursor, {
        match,
        count: 100,
      });
      allKeys.push(...keys);
      cursor = nextCursor;
    } while (cursor !== 0);

    return allKeys;
  }

  public async executeBatch(operations: BatchOperation[]): Promise<void> {
    for (const op of operations) {
      switch (op.type) {
        case "set":
          await this.set(op.key, op.value);
          break;
        case "del":
          await this.delete(op.key);
          break;
        case "zadd":
          await this.zadd(op.key, op.score, op.member);
          break;
        case "zrem":
          await this.zrem(op.key, op.member);
          break;
      }
    }
  }

  public async getStatus(): Promise<KVClientStatus> {
    const pendingQueue = await this.db.syncQueue
      .where("status")
      .equals("pending")
      .toArray();

    return {
      mode: import.meta.env.VITE_USE_MOCK_KV ? "mock" : "vercel",
      isOnline: navigator.onLine,
      syncInProgress: this.syncInProgress,
      pendingOperations: pendingQueue,
      queueSize: pendingQueue.length,
    };
  }

  public async clearCache(): Promise<void> {
    await this.db.clearAllData();
  }

  // Sync Operations
  public async kvGetSyncFromRemote(key: string): Promise<boolean> {
    return this.kvOps.syncFromRemote(key);
  }

  public async zsetSyncFromRemote(key: string): Promise<boolean> {
    return this.zsetOps.syncFromRemote(key);
  }

  protected startPeriodicSync(): void {
    setInterval(() => {
      if (navigator.onLine && !this.syncInProgress) {
        this.processSyncQueue().catch((error) =>
          console.error("Error in periodic sync:", error),
        );
      }
    }, this.syncInterval);
  }

  private initializeProcessEnv(): void {
    if (typeof window !== "undefined" && !window.process) {
      (
        window as unknown as { process?: { env: Record<string, string> } }
      ).process = {
        env: {
          VITE_KV_REST_API_URL: import.meta.env.VITE_KV_REST_API_URL as string,
          VITE_KV_REST_API_TOKEN: import.meta.env
            .VITE_KV_REST_API_TOKEN as string,
          VITE_USE_MOCK_KV: import.meta.env.VITE_USE_MOCK_KV as string,
        },
      };
    }
  }

  private async initializeRemoteClient(): Promise<RemoteClient | null> {
    try {
      const useMockKV = import.meta.env.VITE_USE_MOCK_KV as string;
      const kvUrl = import.meta.env.VITE_KV_REST_API_URL as string;
      const kvToken = import.meta.env.VITE_KV_REST_API_TOKEN as string;

      console.log("KV configuration:", {
        useMockKV,
        hasUrl: !!kvUrl,
        hasToken: !!kvToken?.substring(0, 4),
      });

      if (useMockKV && useMockKV !== "false") {
        console.log("Initializing mock client");
        return await createMockClient();
      }

      if (kvUrl && kvToken) {
        console.log("Initializing Vercel KV client");
        const vercelClient = createClient({
          url: kvUrl,
          token: kvToken,
        });
        return new VercelKVAdapter(vercelClient);
      }

      console.warn("No valid KV configuration found");
      return null;
    } catch (error) {
      console.error("Error initializing remote client:", error);
      return null;
    }
  }

  private setupNetworkListeners(): void {
    window.addEventListener("online", () => {
      console.log("🟢 Application is online - processing sync queue...");
      this.processSyncQueue().catch((error) =>
        console.error("Error processing sync queue:", error),
      );
    });

    window.addEventListener("offline", () => {
      console.log("🔴 Application is offline - changes will be queued");
      this.syncInProgress = false;
    });
  }

  private async processSyncQueue(): Promise<void> {
    if (this.syncInProgress || !navigator.onLine) {
      return;
    }

    try {
      this.syncInProgress = true;
      await Promise.all([
        this.kvOps.processSyncQueue(),
        this.zsetOps.processSyncQueue(),
      ]);
    } finally {
      this.syncInProgress = false;
    }
  }
}

// Export singleton instance
export const kvClient = UnifiedKVClient.getInstance();

// Initialize client
await (async () => {
  const kvClient = await UnifiedKVClient.getInitializedInstance();
  kvClient.initialize(); // Use the client
})();

export async function kvGet<T>(
  key: string,
  options: GetOptions<T> = {},
): Promise<T | null> {
  return kvClient.get<T>(key, options);
}

export async function kvGetSyncFromRemote(key: string): Promise<boolean> {
  return kvClient.kvGetSyncFromRemote(key);
}

export async function kvZoperationsSyncFromRemote(
  key: string,
): Promise<boolean> {
  return kvClient.zsetSyncFromRemote(key);
}

export async function kvScan(
  cursor = 0,
  options: { match?: string; count?: number } = {},
): Promise<[number, string[]]> {
  return kvClient.scan(cursor, options);
}

export async function kvGetAllKeys(match?: string): Promise<string[]> {
  return kvClient.getAllKeys(match);
}

export async function kvZrange<T>(
  key: string,
  start: number | string = 0,
  end: number | string = -1,
  options: ZRangeOptions = { method: "rank" },
): Promise<ZSetResponse<T>[]> {
  if (
    isValidZRangeByRank(options) &&
    typeof start === "number" &&
    typeof end === "number"
  ) {
    return kvClient.zrangeByRank<T>(key, start, end, options);
  }
  if (
    isValidZRangeByScore(options) &&
    (typeof start === "number" || start === "-inf" || start === "+inf") &&
    (typeof end === "number" || end === "-inf" || end === "+inf")
  ) {
    return kvClient.zrangeByScore<T>(key, start, end, options);
  }
  if (
    isValidZRangeByLex(options) &&
    typeof start === "string" &&
    (start.startsWith("(") ||
      start.startsWith("[") ||
      start === "-" ||
      start === "+") &&
    typeof end === "string" &&
    (end.startsWith("(") || end.startsWith("[") || end === "-" || end === "+")
  ) {
    return kvClient.zrangeByLex<T>(
      key,
      start as `(${string}` | `[${string}` | "-" | "+",
      end as `(${string}` | `[${string}` | "-" | "+",
      options,
    );
  }
  throw new Error("Invalid zrange start, end and options");
}

export async function getKVStatus(): Promise<KVClientStatus> {
  return kvClient.getStatus();
}

export async function clearKVCache(): Promise<void> {
  return kvClient.clearCache();
}

export async function refreshKVStore(): Promise<void> {
  return UnifiedKVClient.startForceRefresh();
}

export async function kvSet<T = any>(
  key: string,
  value: T,
  syncMetadata?: SyncMetadata,
): Promise<void> {
  await kvClient.set(key, value);

  if (syncMetadata) {
    await SyncService.recordChange(syncMetadata, key, "UPDATED", "get");
  }
}

export async function kvDel(
  key: string,
  syncMetadata?: SyncMetadata,
): Promise<void> {
  await kvClient.delete(key);

  if (syncMetadata) {
    await SyncService.recordChange(syncMetadata, key, "DELETED", "get");
  }
}

export async function kvZadd<T>(
  key: string,
  score: number,
  member: T,
  syncMetadata?: SyncMetadata,
): Promise<void> {
  await kvClient.zadd(key, score, member);

  if (syncMetadata) {
    await SyncService.recordChange(syncMetadata, key, "MEMBER_ADDED", "zrange");
  }
}

export async function kvZrem<T>(
  key: string,
  member: T,
  syncMetadata?: SyncMetadata,
): Promise<void> {
  await kvClient.zrem(key, member);

  if (syncMetadata) {
    await SyncService.recordChange(
      syncMetadata,
      key,
      "MEMBER_REMOVED",
      "zrange",
    );
  }
}

export async function kvZremrangebyscore(
  key: string,
  min: number,
  max: number,
  syncMetadata?: SyncMetadata,
): Promise<void> {
  await kvClient.zremrangebyscore(key, min, max);

  if (syncMetadata) {
    await SyncService.recordChange(syncMetadata, key, "SET_UPDATED", "zrange");
  }
}

export async function kvBatch(operations: BatchOperation[]): Promise<void> {
  // First execute all operations
  await kvClient.executeBatch(operations);

  // Then record all sync events
  const syncOperations = operations.filter((op) => op.syncMetadata);
  console.log("syncOperations", syncOperations);
  for (const op of syncOperations) {
    if (!op.syncMetadata) continue; // TypeScript guard

    switch (op.type) {
      case "set":
        await SyncService.recordChange(
          op.syncMetadata,
          op.key,
          "UPDATED",
          "get",
        );
        break;
      case "del":
        await SyncService.recordChange(
          op.syncMetadata,
          op.key,
          "DELETED",
          "get",
        );
        break;
      case "zadd":
        await SyncService.recordChange(
          op.syncMetadata,
          op.key,
          "MEMBER_ADDED",
          "zrange",
        );
        break;
      case "zrem":
        await SyncService.recordChange(
          op.syncMetadata,
          op.key,
          "MEMBER_REMOVED",
          "zrange",
        );
        break;
    }
  }
}
